/**
 * Registry of custom flat-design plate pictograms and reusable subcomponents.
 *
 * Each plate can have an authentic, crafted SVG in `src/pictograms/svg/{plateId}.svg`.
 * Reusable visual elements (emblems, crowns, euro bands, separators) live in
 * `src/pictograms/components/{name}.svg`.
 *
 * Vite bundles these at build time via `import.meta.glob`, so everything is
 * precached by the service worker and loads offline with zero HTTP requests.
 */

// Vite glob import of raw SVG strings for full plates
const plateModules = import.meta.glob('./svg/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

// Vite glob import of reusable subcomponents
const componentModules = import.meta.glob('./components/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

// Map of plateId -> raw SVG string
const PICTOGRAM_MAP = new Map<string, string>();

for (const [path, content] of Object.entries(plateModules)) {
  const match = path.match(/\/([^/]+)\.svg$/);
  const plateId = match?.[1];
  if (plateId && typeof content === 'string') {
    PICTOGRAM_MAP.set(plateId, content.trim());
  }
}

// Map of componentName -> normalized SVG symbol / group string
const COMPONENT_MAP = new Map<string, { raw: string; symbol: string }>();

for (const [path, content] of Object.entries(componentModules)) {
  const match = path.match(/\/([^/]+)\.svg$/);
  const name = match?.[1];
  if (name && typeof content === 'string') {
    const trimmed = content.trim();
    const symbolDef = normalizeToSymbol(name, trimmed);
    COMPONENT_MAP.set(name, { raw: trimmed, symbol: symbolDef });
  }
}

/**
 * Normalizes a component SVG string into an SVG `<symbol id="comp-{name}" ...>`
 * or `<g id="comp-{name}" ...>` suitable for inclusion in `<defs>`.
 */
export function normalizeToSymbol(name: string, svgContent: string): string {
  const id = `comp-${name}`;

  // If already a symbol or group with id, ensure correct id
  if (svgContent.startsWith('<symbol') || svgContent.startsWith('<g')) {
    if (!svgContent.includes(`id="${id}"`)) {
      return svgContent.replace(/^<(symbol|g)\b([^>]*)>/i, `<$1 id="${id}" $2>`);
    }
    return svgContent;
  }

  // Extract viewBox from <svg viewBox="..."> if present
  const viewBoxMatch = svgContent.match(/viewBox=["']([^"']+)["']/i);
  const viewBoxAttr = viewBoxMatch?.[1] ? `viewBox="${viewBoxMatch[1]}"` : '';

  // Extract inner contents between <svg...> and </svg>
  const innerMatch = svgContent.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
  const innerContent = innerMatch?.[1] ? innerMatch[1].trim() : svgContent;

  if (viewBoxAttr) {
    return `<symbol id="${id}" ${viewBoxAttr}>${innerContent}</symbol>`;
  }
  return `<g id="${id}">${innerContent}</g>`;
}

/**
 * Injects referenced subcomponents into the SVG's <defs> block so the SVG
 * is self-contained and renders correctly everywhere.
 */
export function injectSubcomponents(svgString: string): string {
  if (!svgString.includes('#comp-')) {
    return svgString;
  }

  // Find all referenced comp-* IDs
  const matches = svgString.match(/#comp-([a-zA-Z0-9_-]+)/g);
  if (!matches) {
    return svgString;
  }

  const referencedSymbols: string[] = [];
  const seen = new Set<string>();

  for (const ref of matches) {
    const name = ref.replace(/^#comp-/, '');
    if (!seen.has(name)) {
      seen.add(name);
      const comp = COMPONENT_MAP.get(name);
      if (comp) {
        referencedSymbols.push(comp.symbol);
      }
    }
  }

  if (referencedSymbols.length === 0) {
    return svgString;
  }

  const defsContent = referencedSymbols.join('\n  ');

  // If svg has an existing <defs>, inject inside it; otherwise insert <defs> after <svg...>
  if (/<defs[\s>]/i.test(svgString)) {
    return svgString.replace(/<defs([^>]*)>/i, `<defs$1>\n  ${defsContent}`);
  }

  return svgString.replace(/<svg\b([^>]*)>/i, `<svg$1>\n  <defs>\n  ${defsContent}\n  </defs>`);
}

/** Check if an authentic, flat-design SVG pictogram exists for the given plate ID. */
export function hasCustomPictogram(plateId: string): boolean {
  return PICTOGRAM_MAP.has(plateId);
}

/** Get the raw SVG content for a plate ID, with any referenced subcomponents injected. */
export function getCustomPictogramSvg(plateId: string): string | undefined {
  const raw = PICTOGRAM_MAP.get(plateId);
  if (!raw) return undefined;
  return injectSubcomponents(raw);
}

/** List all plate IDs that currently have custom SVG pictograms authored. */
export function listCustomPictogramIds(): string[] {
  return Array.from(PICTOGRAM_MAP.keys());
}

/** Total count of authored custom pictograms. */
export function customPictogramCount(): number {
  return PICTOGRAM_MAP.size;
}

/** Check if a reusable subcomponent exists by name. */
export function hasSubcomponent(name: string): boolean {
  return COMPONENT_MAP.has(name);
}

/** Get a reusable subcomponent by name. */
export function getSubcomponent(name: string): { raw: string; symbol: string } | undefined {
  return COMPONENT_MAP.get(name);
}

/** List all available reusable subcomponent names. */
export function listSubcomponentNames(): string[] {
  return Array.from(COMPONENT_MAP.keys());
}
